// The inspection page's side reads — what the queue read does not carry.
//
// get_inspection_dashboard returns a pallet's run id and variety, but not the
// run's number or grower, and no pallet's SSCC. Changing it needs a migration,
// which this change does not have, so the page asks for the few extra facts
// itself (F03 UF-02 / UF-04):
//   - the run number and grower for the runs on the queue — a handful of runs,
//     one read keyed on their ids;
//   - a consignment's pallets, for its With PPECB card — one paged read per
//     consignment, only while its list is open;
//   - the pallet a scanned SSCC belongs to.
// Every read is RLS-scoped to the business; the keys carry the tenant so a
// switch never shows the last business's runs for a beat.
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { fetchAllPaged, type PageResult } from '@/lib/fetchAllPaged'
import { fetchAllPagedIn } from '@/lib/inChunks'
import { ssccMatchCandidates } from '@/lib/ssccLookup'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { POLL } from '@/lib/pollTiers'
import { ssccOf } from './inspectionQueue'

export interface RunName { run_id: string; run_number: string; grower: string | null }

/** The distinct run ids on the queue, sorted — the run-names read's key. */
export function runIdsOf(runIds: readonly (string | null | undefined)[]): string[] {
  return [...new Set(runIds.filter((id): id is string => !!id))].sort()
}

/**
 * Run number + grower for each run on the queue, keyed by run id. A run that
 * was since deleted is still named: its pallet is on the queue either way.
 */
export function useRunNames(runIds: readonly (string | null | undefined)[]) {
  const tenantId = useActiveTenantId()
  const ids = runIdsOf(runIds)
  return useQuery({
    queryKey: ['inspection-run-names', tenantId, ids.join(',')],
    enabled: !!tenantId && ids.length > 0,
    // The set of runs changes as pallets come and go; keep naming the runs
    // already known while the next set loads, rather than blanking every card
    // — but only within one business: another business's names are never a
    // placeholder for this one's.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === tenantId ? prev : undefined),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { rows, error } = await fetchAllPagedIn<RunName, string>(ids, (chunk, from, to) =>
        withReadTimeout(
          supabase.from('runs').select('run_id, run_number, grower').in('run_id', chunk).order('run_id').range(from, to),
          'Inspection: run names',
        ))
      if (error) throw new Error(error)
      return new Map(rows.map(r => [r.run_id, r]))
    },
  })
}

export interface ConsignmentPallet {
  pallet_id: string
  ssc_id:    string | null
  status:    string
  grade:     string | null
  size_code: string | null
  run_id:    string | null
  runs:      { run_number: string | null; variety: string | null } | null
}

/**
 * Every pallet on one PPECB consignment, page by page past PostgREST's
 * 1 000-row cap (fetchAllPaged). A failed page throws — a short list is never
 * passed off as the whole consignment.
 */
export async function readConsignmentPallets(consignmentId: string): Promise<ConsignmentPallet[]> {
  const { rows, error } = await fetchAllPaged<ConsignmentPallet>((from, to) =>
    withReadTimeout(
      supabase.from('pallets')
        .select('pallet_id, ssc_id, status, grade, size_code, run_id, runs(run_number, variety)')
        .eq('ppecb_consignment_id', consignmentId)
        .is('deleted_at', null)
        .order('pallet_id')
        // pallets.run_id → runs is many-to-one, so PostgREST embeds ONE
        // run object; the untyped select infers an array.
        .range(from, to) as unknown as PromiseLike<PageResult<ConsignmentPallet>>,
      'Inspection: consignment pallets',
    ))
  if (error) throw new Error(error)
  return rows
}

/**
 * The pallets on one PPECB consignment, read only while its list is open. A
 * recalled consignment has none: the recall frees its pallets
 * (update-ppecb-consignment clears their ppecb_consignment_id).
 */
export function useConsignmentPallets(consignmentId: string, enabled: boolean) {
  const tenantId = useActiveTenantId()
  return useQuery({
    queryKey: ['inspection-consignment-pallets', tenantId, consignmentId],
    enabled: enabled && !!tenantId,
    queryFn: () => readConsignmentPallets(consignmentId),
  })
}

export interface SsccHit { pallet_id: string; status: string }

const NO_ROWS = { data: [] as SsccHit[], error: null }

/**
 * The pallet a scanned label's SSCC belongs to, or null when none does.
 * Throws when a read fails — "no pallet" is never said over a failed read.
 *
 * Both SSCC lanes, as GlobalPalletSearch reads them (src/lib/ssccLookup.ts):
 * `ssc_id`, the SSCC this packhouse mints — canonical, so one exact match on a
 * valid code — and `sscc`, another packer's label on a pallet received in
 * packed, matched as captured and NOT gated on the check digit. Call it for a
 * scan that named no queue pallet by its number.
 */
export async function palletBySscc(raw: string): Promise<SsccHit | null> {
  const minted   = ssccOf(raw)
  const received = ssccMatchCandidates(raw)
  const [own, theirs] = await Promise.all([
    minted
      ? withReadTimeout(
          supabase.from('pallets').select('pallet_id, status').eq('ssc_id', minted).is('deleted_at', null).limit(1),
          'Inspection: pallet by SSCC',
        )
      : NO_ROWS,
    received.length
      ? withReadTimeout(
          // No unique constraint on `sscc`: take the first, never maybeSingle().
          supabase.from('pallets').select('pallet_id, status').in('sscc', received).is('deleted_at', null).limit(1),
          'Inspection: pallet by received SSCC',
        )
      : NO_ROWS,
  ])
  if (own.error) throw own.error
  if (theirs.error) throw theirs.error
  return (own.data as SsccHit[] | null)?.[0] ?? (theirs.data as SsccHit[] | null)?.[0] ?? null
}

/** One unreleased QC FAIL on a run: the check to open, and its run. */
export interface QcHold { qc_run_id: string; run_id: string }

interface FailRow {
  qc_run_id:     string
  source_run_id: string | null
  completed_at:  string | null
  started_at:    string | null
}

/**
 * One hold per run: its newest unreleased FAIL (by completion, else start).
 * A run with two failed checks is blocked either way; the card opens one.
 */
export function holdsByRun(rows: readonly FailRow[]): Map<string, QcHold> {
  const newest = new Map<string, FailRow>()
  const at = (r: FailRow) => r.completed_at ?? r.started_at ?? ''
  for (const r of rows) {
    if (!r.source_run_id) continue
    const prev = newest.get(r.source_run_id)
    if (!prev || at(r) > at(prev) || (at(r) === at(prev) && r.qc_run_id > prev.qc_run_id)) newest.set(r.source_run_id, r)
  }
  return new Map([...newest].map(([runId, r]) => [runId, { qc_run_id: r.qc_run_id, run_id: runId }]))
}

/**
 * The unreleased QC FAILs on these runs — the predicate the approve trigger
 * (mig 20260703122000) refuses on, via qc_fail_blocking_runs (mig
 * 20260703121000): result FAIL, not dispositioned, not deleted, this
 * business. Read straight off qc_runs (tenant-readable, mig 415), as the
 * dispatch gate does (src/lib/qcDispatchGate.ts), because the card needs the
 * CHECK to open, which that function does not return.
 */
export async function readQcHolds(tenantId: string, runIds: readonly string[]): Promise<Map<string, QcHold>> {
  const { rows, error } = await fetchAllPagedIn<FailRow, string>(runIds, (chunk, from, to) =>
    withReadTimeout(
      supabase.from('qc_runs')
        .select('qc_run_id, source_run_id, completed_at, started_at')
        .eq('tenant_id', tenantId)
        .eq('result', 'FAIL').is('dispositioned_at', null).is('deleted_at', null)
        .in('source_run_id', chunk)
        .order('qc_run_id')
        .range(from, to),
      'Inspection: QC holds',
    ))
  if (error) throw new Error(error)
  return holdsByRun(rows)
}

/**
 * The QC hold on each run on the queue, keyed by run id (F03 UF-05): the
 * card says so and its Approve reads "Blocked by QC" before anyone taps it.
 * Polled with the queue, so a release on the QC page clears the card within
 * one poll. A pallet whose fruit came from a build-up line is not flagged
 * here — the database still refuses it, and the refusal names the check
 * (blockingCheckFor).
 */
export function useQcHolds(runIds: readonly (string | null | undefined)[]) {
  const tenantId = useActiveTenantId()
  const ids = runIdsOf(runIds)
  return useQuery({
    queryKey: ['inspection-qc-holds', tenantId, ids.join(',')],
    enabled: !!tenantId && ids.length > 0,
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === tenantId ? prev : undefined),
    refetchInterval: POLL.live,
    queryFn: () => readQcHolds(tenantId as string, ids),
  })
}

/**
 * The failed check behind one pallet's refused approval — its own run or a
 * build-up line's source run (qc_fail_blocking_runs, granted to signed-in
 * users on their own business), or null when none is found any more.
 */
export async function blockingCheckFor(tenantId: string, palletId: string): Promise<QcHold | null> {
  const { data, error } = await withReadTimeout(
    supabase.rpc('qc_fail_blocking_runs', { p_pallet_ids: [palletId] }),
    'Inspection: QC hold on a pallet',
  )
  if (error) throw error
  const runIds = runIdsOf(((data ?? []) as { source_run_id: string }[]).map(r => r.source_run_id))
  if (runIds.length === 0) return null
  const holds = await readQcHolds(tenantId, runIds)
  return holds.get(runIds.find(id => holds.has(id)) ?? '') ?? null
}

/** Where an approved pallet stands on a truck (F03 UF-13). `consignment` is
 *  the truck consignment's number; `mission` is a loading mission with no
 *  consignment (then the pallet is planned on a truck without a number). */
export interface TruckPlan { consignment: string | null; mission: boolean }

interface PlanRow {
  pallet_id: string
  truck_consignment_id: string | null
  loading_mission_id:   string | null
  // many-to-one embed: one object, though the untyped select infers an array
  truck_consignments: { consignment_number: string | null } | null
}

/** One plan per pallet read; a pallet the read did not return has no entry. */
function truckPlansOf(rows: readonly PlanRow[]): Map<string, TruckPlan> {
  return new Map(rows.map(r => [r.pallet_id, {
    consignment: r.truck_consignment_id ? (r.truck_consignments?.consignment_number ?? 'a truck') : null,
    mission: !!r.loading_mission_id && !r.truck_consignment_id,
  }]))
}

/** Truck consignment + loading mission for these pallets, tenant-filtered and
 *  chunked (the dashboard RPC returns every approved pallet, unpaged). */
export async function readTruckPlans(tenantId: string, palletIds: readonly string[]): Promise<Map<string, TruckPlan>> {
  const { rows, error } = await fetchAllPagedIn<PlanRow, string>(palletIds, (chunk, from, to) =>
    withReadTimeout(
      supabase.from('pallets')
        .select('pallet_id, truck_consignment_id, loading_mission_id, truck_consignments!pallets_truck_consignment_id_fkey(consignment_number)')
        .eq('tenant_id', tenantId)
        .in('pallet_id', chunk)
        .order('pallet_id')
        .range(from, to) as unknown as PromiseLike<PageResult<PlanRow>>,
      'Inspection: truck plans',
    ))
  if (error) throw new Error(error)
  return truckPlansOf(rows)
}

/**
 * Whether each approved pallet is planned on a truck, keyed by pallet id.
 * A failed or pending read yields no entry: the card then says nothing, never
 * "Not on a truck" over a read that did not answer.
 */
export function useTruckPlans(palletIds: readonly string[]) {
  const tenantId = useActiveTenantId()
  const ids = [...new Set(palletIds)].sort()
  return useQuery({
    queryKey: ['inspection-truck-plans', tenantId, ids.join(',')],
    enabled: !!tenantId && ids.length > 0,
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === tenantId ? prev : undefined),
    refetchInterval: POLL.live,
    queryFn: () => readTruckPlans(tenantId as string, ids),
  })
}

/** When an approved pallet was inspected, and its run's commodity: what the
 *  house InspectionAgeChip needs (F03 UF-15). */
export interface InspectionDate { inspected_at: string | null; commodity: string | null }

interface DateRow { pallet_id: string; inspected_at: string | null; runs: { commodity: string | null } | null }

async function readInspectionDates(tenantId: string, palletIds: readonly string[]): Promise<Map<string, InspectionDate>> {
  const { rows, error } = await fetchAllPagedIn<DateRow, string>(palletIds, (chunk, from, to) =>
    withReadTimeout(
      supabase.from('pallets')
        .select('pallet_id, inspected_at, runs(commodity)')
        .eq('tenant_id', tenantId)
        .in('pallet_id', chunk)
        .order('pallet_id')
        // many-to-one embed: one object, though the untyped select infers an array
        .range(from, to) as unknown as PromiseLike<PageResult<DateRow>>,
      'Inspection: inspection dates',
    ))
  if (error) throw new Error(error)
  return new Map(rows.map(r => [r.pallet_id, { inspected_at: r.inspected_at, commodity: r.runs?.commodity ?? null }]))
}

/** Inspection date + commodity per approved pallet. A failed or pending read
 *  yields no entry: the card then shows no chip, never a "fresh" claim. */
export function useInspectionDates(palletIds: readonly string[]) {
  const tenantId = useActiveTenantId()
  const ids = [...new Set(palletIds)].sort()
  return useQuery({
    queryKey: ['inspection-dates', tenantId, ids.join(',')],
    enabled: !!tenantId && ids.length > 0,
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === tenantId ? prev : undefined),
    staleTime: 5 * 60_000,
    queryFn: () => readInspectionDates(tenantId as string, ids),
  })
}
